'use client';

import { logout, User } from '@/app/actions/user';
import { ThemeSwitch } from '@/components/theme-switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getUserAvatarSrc } from '@/lib/avatar';
import { MCP_URL, OPENAPI_URL } from '@/lib/constants';
import {
  BarChart3Icon,
  BookOpen,
  CircleStopIcon,
  CreditCardIcon,
  Globe,
  KeyRoundIcon,
  LogOutIcon,
  PaletteIcon,
  ReceiptTextIcon,
  SquareArrowOutUpRightIcon,
  UserIcon,
  UserPlusIcon,
} from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { McpIcon } from './icons';

interface UserDropdownProps {
  user: User;
}

interface CreditsDropdownProps {
  user: User;
}

interface UserProfileMenuContentProps {
  onNavigate?: () => void;
}

function CreditsDropdown({ user }: CreditsDropdownProps) {
  const [isOpenCredits, setIsOpenCredits] = useState(false);

  return (
    <DropdownMenu open={isOpenCredits} onOpenChange={setIsOpenCredits}>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="h-8 w-8 rounded-full px-0 text-xs md:h-7 md:w-auto md:rounded-md md:px-2"
          aria-label={`我的积分：${user.pointInfo.available.toLocaleString()}`}
        >
          <CircleStopIcon className="w-3 h-3" />
          <span className="hidden font-medium text-sm md:inline">
            {user.pointInfo.available.toLocaleString()}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-xl shadow-none">
        <DropdownMenuLabel>我的积分</DropdownMenuLabel>
        <DropdownMenuGroup>
          <DropdownMenuItem
            className="opacity-100 cursor-default"
            onSelect={(e) => e.preventDefault()}
          >
            <span>可用积分</span>
            <span className="ml-auto opacity-60 font-medium">
              {user.pointInfo.available.toLocaleString()}
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="opacity-100 cursor-default"
            onSelect={(e) => e.preventDefault()}
          >
            <span>累计获取</span>
            <span className="ml-auto opacity-60 font-medium">
              {user.pointInfo.accumulated.toLocaleString()}
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="opacity-100 cursor-default"
            onSelect={(e) => e.preventDefault()}
          >
            <span>API积分</span>
            <span className="ml-auto opacity-60 font-medium">
              {user.apiPointInfo?.available?.toLocaleString() || 0}
            </span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="p-0 mb-1"
          onSelect={(e) => e.preventDefault()}
        >
          <Button
            size="sm"
            color="primary"
            className="w-full h-7"
            asChild
            onClick={() => setIsOpenCredits(false)}
          >
            <Link href="/pricing/credits">购买积分</Link>
          </Button>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserProfileMenuContent({ onNavigate }: UserProfileMenuContentProps) {
  return (
    <>
      <DropdownMenuGroup>
        <DropdownMenuItem asChild>
          <Link href="/profile" className="cursor-pointer" onClick={onNavigate}>
            <UserIcon className="h-4 w-4" />
            个人资料
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link
            href="/card-pack"
            className="cursor-pointer flex items-center"
            onClick={onNavigate}
          >
            <CreditCardIcon className="h-4 w-4" />
            我的卡包
            <span className="ml-auto text-[10px] font-medium bg-red-500 text-white px-1.5 py-0.5 rounded-full leading-none">
              NEW
            </span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link
            href="/my-invite"
            className="cursor-pointer"
            onClick={onNavigate}
          >
            <UserPlusIcon className="h-4 w-4" />
            我的邀请
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            href="/my-share"
            className="cursor-pointer"
            onClick={onNavigate}
          >
            <Globe className="h-4 w-4" />
            公开内容
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            href="/credits/usage"
            className="cursor-pointer"
            onClick={onNavigate}
          >
            <BarChart3Icon className="h-4 w-4" />
            积分用量
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link
            href="/credits/billing"
            className="cursor-pointer"
            onClick={onNavigate}
          >
            <ReceiptTextIcon className="h-4 w-4" />
            积分明细
          </Link>
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuItem asChild>
          <Link
            href="/api-keys"
            className="cursor-pointer"
            onClick={onNavigate}
          >
            <KeyRoundIcon className="h-4 w-4" />
            API Keys
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a
            href={OPENAPI_URL}
            target="_blank"
            className="cursor-pointer"
            onClick={onNavigate}
          >
            <BookOpen className="h-4 w-4" />
            接口文档
            <SquareArrowOutUpRightIcon className="ml-auto h-4 w-4" />
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a
            href={MCP_URL}
            target="_blank"
            className="cursor-pointer"
            onClick={onNavigate}
          >
            <McpIcon className="h-4 w-4" />
            MCP 服务
            <SquareArrowOutUpRightIcon className="ml-auto h-4 w-4" />
          </a>
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuItem
          className="cursor-default"
          onSelect={(e) => e.preventDefault()}
        >
          <PaletteIcon className="h-4 w-4" />
          <span>主题</span>
          <span className="ml-auto">
            <ThemeSwitch />
          </span>
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onClick={async () => {
          await logout();
          window.location.href = '/';
        }}
      >
        <LogOutIcon className="h-4 w-4" />
        退出登录
      </DropdownMenuItem>
    </>
  );
}

const UserDropdown = ({ user }: UserDropdownProps) => {
  const [isOpenProfile, setIsOpenProfile] = useState(false);

  return (
    <>
      <CreditsDropdown user={user} />

      <DropdownMenu open={isOpenProfile} onOpenChange={setIsOpenProfile}>
        <DropdownMenuTrigger asChild>
          <div className="flex items-center gap-2 my-1 cursor-pointer">
            <Avatar className="h-7 w-7 shrink-0">
              <AvatarImage src={getUserAvatarSrc(user)} alt={user.nickname} />
              <AvatarFallback>{user.nickname?.[0] || '?'}</AvatarFallback>
            </Avatar>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-xl shadow-none">
          <UserProfileMenuContent onNavigate={() => setIsOpenProfile(false)} />
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

export { CreditsDropdown, UserProfileMenuContent };
export default UserDropdown;
